import { useEffect, useState } from 'react';
import { ThermometerSun, MapPin, Sparkles, ChevronRight } from 'lucide-react';
import type { Product } from '../../types';
import { formatGpsCoords } from '../../lib/poi';
import { fetchSoilWeatherData, type SoilWeatherData } from '../../lib/weather';
import { getPlantingGuide } from '../../lib/faunamaster-guides';

interface Props {
  centroid: { lat: number; lng: number } | null;
  selectedBlendId?: string;
  products: Product[];
  onNavigateToSeeding?: () => void;
}

export function FieldSoilWeatherCard({
  centroid,
  selectedBlendId,
  products,
  onNavigateToSeeding,
}: Props) {
  const [data, setData] = useState<SoilWeatherData | null>(null);
  const [loading, setLoading] = useState(false);

  const lat = centroid?.lat ?? 55.4;
  const lng = centroid?.lng ?? 11.8;

  useEffect(() => {
    if (!centroid) return;
    let cancelled = false;
    setLoading(true);

    fetchSoilWeatherData(lat, lng)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [centroid, lat, lng]);

  if (!centroid) return null;

  const currentSoilTemp = data?.current.soilTemperature6cm ?? null;
  const moisturePct = data ? Math.round(data.current.soilMoisture0to1cm * 100) : null;

  const selectedProduct = selectedBlendId ? products.find((p) => p.id === selectedBlendId) : null;
  const guide = selectedProduct ? getPlantingGuide(selectedProduct.name) : null;

  // Bestem om temperaturen er passende for denne specifikke blanding
  let blendStatus: 'optimal' | 'prep' | 'cold' | 'none' = 'none';
  let blendAdvice = '';

  if (currentSoilTemp !== null) {
    if (guide) {
      if (currentSoilTemp >= guide.soilTempMinC) {
        blendStatus = 'optimal';
        blendAdvice = `Jorden er ${currentSoilTemp.toFixed(1)} °C. Optimal for ${guide.productName} (kræver min. ${guide.soilTempMinC} °C).`;
      } else if (currentSoilTemp >= guide.soilTempMinC - 2) {
        blendStatus = 'prep';
        blendAdvice = `Jorden er ${currentSoilTemp.toFixed(1)} °C. Gør såbed klar; afvent ${guide.soilTempMinC} °C før såning.`;
      } else {
        blendStatus = 'cold';
        blendAdvice = `Jorden er ${currentSoilTemp.toFixed(1)} °C. For kold til ${guide.productName} (kræver min. ${guide.soilTempMinC} °C).`;
      }
    } else {
      if (currentSoilTemp >= 10) blendStatus = 'optimal';
      else if (currentSoilTemp >= 7) blendStatus = 'prep';
      else blendStatus = 'cold';
    }
  }

  return (
    <div
      style={{
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        padding: '10px 12px',
        marginTop: 10,
        marginBottom: 10,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 6,
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            fontSize: 11,
            fontWeight: 700,
            color: '#334155',
          }}
        >
          <ThermometerSun size={13} style={{ color: '#0369a1' }} />
          <span>Markens mikroklima (lige her)</span>
        </span>
        <span style={{ fontSize: 10, color: '#64748b' }}>GPS: {formatGpsCoords(lat, lng)}</span>
      </div>

      {loading && !data ? (
        <div style={{ fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>
          Henter lokal jordtemperatur for marken...
        </div>
      ) : currentSoilTemp !== null ? (
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 4,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
              {currentSoilTemp.toFixed(1)} °C{' '}
              <span style={{ fontSize: 11, fontWeight: 500, color: '#64748b' }}>
                i såbed (6 cm) · {moisturePct}% fugt
              </span>
            </div>
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                padding: '2px 6px',
                borderRadius: 4,
                background:
                  blendStatus === 'optimal'
                    ? '#dcfce7'
                    : blendStatus === 'prep'
                      ? '#fef9c3'
                      : '#e0f2fe',
                color:
                  blendStatus === 'optimal'
                    ? '#166534'
                    : blendStatus === 'prep'
                      ? '#854d0e'
                      : '#0369a1',
              }}
            >
              {blendStatus === 'optimal'
                ? 'Optimal'
                : blendStatus === 'prep'
                  ? 'Forbered'
                  : 'For kold'}
            </span>
          </div>

          {blendAdvice && (
            <div
              style={{
                fontSize: 11,
                lineHeight: 1.35,
                color: blendStatus === 'optimal' ? '#166534' : '#854d0e',
                marginTop: 4,
              }}
            >
              {blendAdvice}
            </div>
          )}

          {onNavigateToSeeding && (
            <div style={{ marginTop: 6, textAlign: 'right' }}>
              <button
                type="button"
                className="link-button"
                onClick={onNavigateToSeeding}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 3,
                  fontSize: 11,
                  color: '#15803d',
                  fontWeight: 600,
                }}
              >
                <span>Såningsvejledning &amp; maskinindstilling</span>
                <ChevronRight size={12} />
              </button>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
